import { reactive, readonly } from 'vue'

const STORAGE_KEY = 'translate_collector_data_v4'

/**
 * 收集器状态 - 使用 reactive 实现双向绑定
 */
interface CollectorState {
  /** 未翻译的key集合 */
  untranslatedKeys: string[]
  /** 已翻译的key集合 */
  translatedKeys: string[]
}

// 创建响应式状态 - 模块级别单例
const state = reactive<CollectorState>({
  untranslatedKeys: [],
  translatedKeys: []
})

// 已上报的 key 集合（不加入响应式）
const reportedKeys = new Set<string>()

// 是否启用收集
let isEnabled = false

/**
 * 启动收集
 */
export function startTranslateCollector() {
  isEnabled = true
}

/**
 * 停止收集
 */
export function stopTranslateCollector() {
  isEnabled = false
}

/**
 * 检查是否启用
 */
export function isTranslateCollectorEnabled() {
  return isEnabled
}

/**
 * 收集翻译key
 * @param key 翻译key
 * @param type 翻译类型 1: 未翻译, 2: 已翻译
 */
export function collectTranslateKey(key: string, type: number) {
  if (!isEnabled) return
  if (!key || typeof key !== 'string') return

  const trimmedKey = key.trim()
  if (!trimmedKey) return

  // type 1: 未翻译, type 2: 已翻译
  if (type === 1) {
    if (!state.untranslatedKeys.includes(trimmedKey)) {
      state.untranslatedKeys.push(trimmedKey)
    }
    // 从已翻译中移除
    const translatedIndex = state.translatedKeys.indexOf(trimmedKey)
    if (translatedIndex > -1) {
      state.translatedKeys.splice(translatedIndex, 1)
    }
  } else if (type === 2) {
    if (!state.translatedKeys.includes(trimmedKey)) {
      state.translatedKeys.push(trimmedKey)
    }
    // 从未翻译中移除
    const untranslatedIndex = state.untranslatedKeys.indexOf(trimmedKey)
    if (untranslatedIndex > -1) {
      state.untranslatedKeys.splice(untranslatedIndex, 1)
    }
  }

  // 保存到 localStorage
  saveCollectorState()
}

/**
 * 获取响应式状态 - 用于组件绑定
 */
export function getCollectorState() {
  return readonly(state)
}

/**
 * 获取所有未翻译的keys
 */
export function getUntranslatedKeys(): string[] {
  return state.untranslatedKeys
}

/**
 * 获取所有已翻译的keys
 */
export function getTranslatedKeys(): string[] {
  return state.translatedKeys
}

/**
 * 获取未翻译数量
 */
export function getUntranslatedCount(): number {
  return state.untranslatedKeys.length
}

/**
 * 获取已翻译数量
 */
export function getTranslatedCount(): number {
  return state.translatedKeys.length
}

/**
 * 获取收集总数
 */
export function getTotalCount(): number {
  return state.untranslatedKeys.length + state.translatedKeys.length
}

/**
 * 清空收集
 */
export function clearCollector() {
  state.untranslatedKeys = []
  state.translatedKeys = []
  reportedKeys.clear()
  saveCollectorState()
}

/**
 * 标记已上报
 */
export function markAsReported(keys: string[]) {
  keys.forEach(key => {
    reportedKeys.add(key)
  })
}

/**
 * 获取未上报的keys
 */
export function getUnreportedKeys(): string[] {
  return state.untranslatedKeys.filter(key => !reportedKeys.has(key))
}

/**
 * 保存到 localStorage
 */
function saveCollectorState() {
  try {
    const data = {
      untranslatedKeys: state.untranslatedKeys,
      translatedKeys: state.translatedKeys,
      reportedKeys: Array.from(reportedKeys)
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('[TranslateCollector] 保存到 localStorage 失败:', e)
  }
}

/**
 * 从 localStorage 恢复
 */
function loadCollectorState() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      if (data.untranslatedKeys) {
        // 使用 push 保持响应式
        data.untranslatedKeys.forEach((key: string) => {
          if (!state.untranslatedKeys.includes(key)) {
            state.untranslatedKeys.push(key)
          }
        })
      }
      if (data.translatedKeys) {
        data.translatedKeys.forEach((key: string) => {
          if (!state.translatedKeys.includes(key)) {
            state.translatedKeys.push(key)
          }
        })
      }
      if (data.reportedKeys) {
        data.reportedKeys.forEach((key: string) => {
          reportedKeys.add(key)
        })
      }
    }
  } catch (e) {
    console.warn('[TranslateCollector] 从 localStorage 恢复失败:', e)
  }
}

// 开发环境默认启动
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  loadCollectorState()
  startTranslateCollector()
}

// 兼容旧版的默认导出
export default {
  start: startTranslateCollector,
  stop: stopTranslateCollector,
  getIsEnabled: isTranslateCollectorEnabled,
  collect: collectTranslateKey,
  getUntranslatedCount,
  getTranslatedCount,
  getTotalCount,
  clear: clearCollector
}

// 为了兼容性，也导出单例对象
export const translateCollector = {
  start: startTranslateCollector,
  stop: stopTranslateCollector,
  getIsEnabled: isTranslateCollectorEnabled,
  collect: collectTranslateKey,
  getUntranslatedCount,
  getTranslatedCount,
  getTotalCount,
  clear: clearCollector,
  getUntranslatedKeys,
  getTranslatedKeys,
  markAsReported,
  getUnreportedKeys
}
